// Neu (SPEC 03 §5.2) — EINE Funktion je Route aus SPEC 02 §2. Bildschirme schreiben nie einen
// Pfad selbst. Pfade gehen OHNE /api an fetchJson (das setzt BASIS = "/api" davor); Adressen
// für src=/href=/EventSource bauen die …Url-Helfer MIT /api.
//
// `frage()` ist wörtlich aus /opt/lilasp-zeitplan/frontend/src/api/client.ts (Zeilen 50–64).
// Listen gehen an UNSER Backend kommagetrennt, wo 02 „a,b,…“ sagt (`spalten`) — die Aufrufer
// fügen sie vorher zusammen; alles andere ist einwertig.
import { ApiError, deleteJson, getJson, patchJson, postJson, putJson } from "../lib/fetchJson";
import { kompakt } from "../lib/rezept";
import { hochladen } from "./hochladen";
import type {
  AbfrageKurz,
  FormularEntwurf,
  FormularFestschreibenErgebnis,
  FormularOp,
  FormularStand,
  FormularVorschau,
  AntwortAnsicht,
  Aufloesung,
  AuftraegeStand,
  AuswertungBestand,
  AuswertungsKatalog,
  AuthConfig,
  BelegAnsicht,
  Belegkarte,
  Bereich,
  BoegenAutomatisch,
  BogenAnsicht,
  BogenGruppe,
  BogenKurz,
  BogenVorschlagSeite,
  Charge,
  Dokument,
  DokumentVoll,
  EingangListe,
  Entwurf,
  EntwurfDaten,
  Ergebnis,
  ErkennenErgebnis,
  Export,
  ExportKopf,
  ExportWunsch,
  Facetten,
  Filter,
  FrageEingabe,
  FragenErgebnis,
  Fragenliste,
  FragenKi,
  Freigabe,
  Gpu,
  Health,
  Ich,
  ImportErgebnis,
  KachelDef,
  KaestchenGuete,
  RahmenStand,
  SeitenanalyseStand,
  SeitenElemente,
  ZeichnungItem,
  ZeichnungenListe,
  Katalog,
  KennzahlDiff,
  Kennzahlen,
  KiStatus,
  KodierEintrag,
  KodierErgebnis,
  LaufStand,
  Maske,
  Menge,
  MengeHeute,
  MengeKopf,
  MengenElement,
  NutzerZeile,
  OberflaechenFehlerBericht,
  OffeneMaske,
  PruefAktion,
  PruefEintrag,
  PruefItem,
  ProtokollZeile,
  Rechteck,
  RegionAufSeite,
  RegionFlags,
  Revision,
  Rezept,
  Rolle,
  Schritt,
  Seite,
  SeiteKurz,
  SeiteMitRegionen,
  SeitenKaestchen,
  StandEreignis,
  Standort,
  StartAntwort,
  Stichprobe,
  Thema,
  ThemaSeite,
  ThemaVorschlag,
  Uuid,
  Verfahren,
  VerfahrenKurz,
  VerfahrenNeu,
  VerifyAntwort,
  Verwendung,
  VorlageKurz,
  Zelle,
  ZugriffZeile,
  Zustand,
  Bezug,
  DateiImport,
  DateiVorschau,
  Datenquelle,
  DatenquelleEingabe,
  DatenquelleKurz,
  DatenquelleTest,
  DbImport,
  DbVorschau,
  QuellDatenbanken,
  QuellTabellen,
  Gemeinde,
  BeitraegeGeo,
  KarteGeo,
  Ort,
  OrtArt,
  OrteImporte,
  OrteStand,
  Ortsbezirk,
  OsmUmfang,
  RegionVerortung,
  Schulort,
  Sicherheit,
  Verortung,
  VerortungAktion,
  VerortungPosten,
} from "./typen";

export { frageAssistent, frageBereich } from "./sse";

/**
 * Suchparameter bauen und dabei leere Werte weglassen. Eine Liste wird zu
 * MEHREREN Parametern desselben Namens (`schlagwort=a&schlagwort=b`) — das
 * Backend verknüpft sie mit UND (§5.4.2).
 */
function frage(params: Record<string, string | number | boolean | string[] | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === "") continue;
    if (Array.isArray(v)) {
      for (const e of v) if (e !== "") p.append(k, e);
      continue;
    }
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

/** Pfadsegment sicher einsetzen (Codes, UUIDs, Slugs). */
const seg = (s: string | number): string => encodeURIComponent(String(s));

/** Filter → base64url(JSON), ohne Vorgaben (leere Listen, pruefstatus "geprueft", aus_zeichnung "alle"). */
export function filterB64(f: Filter): string {
  const json = JSON.stringify(kompakt(f)); // lib/rezept.ts §6.4
  const bytes = new TextEncoder().encode(json);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Blätterparameter aller Listen (02 §1.9). */
export interface Blaettern {
  nach?: string | null;
  limit?: number;
}

/* --- Anmeldung, Ich (02 §2.1) ------------------------------------------------ */

export const holeAuthConfig = () => getJson<AuthConfig>("/auth/config");
// `turnstile` ist die Wertmarke des Widgets (E18). Der Feldname ist der aus StartRumpf im
// Backend (routers/auth.py); `null`, solange kein Sitekey konfiguriert ist — dann prüft der
// Server nicht. Fehlt die Marke bei gesetztem Sitekey: 403 „Die Prüfung ist durchgefallen …“.
export const codeAnfordern = (email: string, turnstile: string | null) =>
  postJson<StartAntwort>("/auth/start", { email, turnstile });
export const codeEinloesen = (challenge: string, code: string, angemeldet_bleiben: boolean) =>
  postJson<VerifyAntwort>("/auth/verify", { challenge, code, angemeldet_bleiben });
export const abmelden = () => postJson<{ ok: boolean }>("/auth/logout");
export const holeIch = () => getJson<Ich>("/me");
export const setzeAnzeigename = (anzeigename: string) => patchJson<Ich>("/me", { anzeigename });
export const holeHealth = () => getJson<Health>("/health");
/** Absturzbericht eines Auffangkorbs (lib/fehlerbericht.ts). `keepalive`: „Neu laden" direkt
 *  nach dem Absturz soll den Bericht nicht abbrechen. Antwort 204 (429 über der Grenze). */
export const meldeOberflaechenFehler = (bericht: OberflaechenFehlerBericht) =>
  postJson<void>("/oberflaeche/fehler", bericht, { keepalive: true });

/* --- Verwaltung --------------------------------------------------------------- */

export const holeNutzer = () => getJson<{ eintraege: NutzerZeile[] }>("/admin/nutzer");
export const aendereNutzer = (id: Uuid, patch: { rolle?: Rolle; aktiv?: boolean }) =>
  patchJson<NutzerZeile>(`/admin/nutzer/${seg(id)}`, patch);
export const entsperre = (email: string) => postJson<{ geloescht: number }>("/admin/entsperren", { email });
/* E29 (05 §18) — Datenquellen: externe PostGIS-Datenbanken, nur lesend. Das Passwort geht nur HIN (anlegen,
   ersetzen), nie zurück. Verwalten: Recht `verwalten`; nutzen (Reiter „Laden“): Recht `orte`. */
export const holeDatenquellen = () => getJson<{ eintraege: Datenquelle[] }>("/admin/datenquellen");
export const legeDatenquelleAn = (b: DatenquelleEingabe) => postJson<Datenquelle>("/admin/datenquellen", b);
export const aendereDatenquelle = (id: string, b: DatenquelleEingabe) => patchJson<Datenquelle>(`/admin/datenquellen/${seg(id)}`, b);
export const testeDatenquelle = (id: string) => postJson<DatenquelleTest>(`/admin/datenquellen/${seg(id)}/test`);
export const holeZugriff = (V: string) => getJson<{ nutzer: ZugriffZeile[] }>(`/v/${seg(V)}/zugriff`);
export const gewaehreZugriff = (V: string, userId: Uuid) => putJson<{ ok: boolean }>(`/v/${seg(V)}/zugriff/${seg(userId)}`);
export const entzieheZugriff = (V: string, userId: Uuid) => deleteJson<{ ok: boolean }>(`/v/${seg(V)}/zugriff/${seg(userId)}`);
export interface ProtokollFilter extends Blaettern {
  art?: string | null;
  verfahren?: string | null;
  von?: string | null;
  bis?: string | null;
}
export const holeProtokoll = (p: ProtokollFilter = {}) =>
  getJson<Seite<ProtokollZeile>>(
    `/admin/protokoll${frage({ art: p.art, verfahren: p.verfahren, von: p.von, bis: p.bis, nach: p.nach, limit: p.limit })}`,
  );

/* --- Verfahren, Charge, Standort, Vorlage (02 §2.2) --------------------------- */

export const holeVerfahrenListe = () => getJson<{ eintraege: VerfahrenKurz[] }>("/verfahren");
export const legeVerfahrenAn = (b: VerfahrenNeu) => postJson<Verfahren>("/verfahren", b);
export const holeVerfahren = (V: string) => getJson<Verfahren>(`/v/${seg(V)}`);
export interface VerfahrenPatch {
  titel?: string;
  kommune?: string;
  avv_ref?: string | null;
  loeschen_bis?: string | null;
  freigabe?: Freigabe;
  notiz?: string | null;
  /** E20: Namen vor dem Sehmodell schwärzen (Recht `verwalten`). Ein Wechsel leitet die ungelesenen Seiten neu ab. */
  schwaerzen?: boolean;
}
export const aendereVerfahren = (V: string, patch: VerfahrenPatch) => patchJson<Verfahren>(`/v/${seg(V)}`, patch);

export const holeChargen = (V: string) => getJson<{ eintraege: Charge[] }>(`/v/${seg(V)}/chargen`);
export interface ChargeEingabe {
  schule: string;
  klasse?: string | null;
  erhebungsdatum?: string | null;
  standort_code?: string | null;
  formularvorlage?: string | null;
  notiz?: string | null;
}
export const legeChargeAn = (V: string, b: ChargeEingabe) => postJson<Charge>(`/v/${seg(V)}/chargen`, b);
export const aendereCharge = (V: string, id: Uuid, b: Partial<ChargeEingabe>) =>
  patchJson<Charge & { hinweis?: string }>(`/v/${seg(V)}/chargen/${seg(id)}`, b);
export const setzeChargenMaske = (V: string, id: Uuid, m: Maske) => putJson<Charge>(`/v/${seg(V)}/chargen/${seg(id)}/maske`, m);

export const holeStandorte = (V: string) => getJson<{ eintraege: Standort[] }>(`/v/${seg(V)}/standorte`);
export const legeStandortAn = (V: string, b: { code: string; name: string; kurz?: string }) =>
  postJson<Standort>(`/v/${seg(V)}/standorte`, b);
export const aendereStandort = (V: string, code: string, b: { name?: string; kurz?: string }) =>
  patchJson<Standort>(`/v/${seg(V)}/standorte/${seg(code)}`, b);

export const holeVorlagen = (V: string) => getJson<{ eintraege: VorlageKurz[] }>(`/v/${seg(V)}/vorlagen`);
export const importiereVorlagen = (V: string, vorlagen: unknown[]) =>
  postJson<{ angelegt: string[] }>(`/v/${seg(V)}/vorlagen`, { vorlagen });

/* --- Upload, Eingang, Dokument, Seite ----------------------------------------- */

export function ladeDokumentHoch(
  V: string,
  chargeId: Uuid,
  datei: File,
  beiFortschritt?: (geladen: number, gesamt: number) => void,
  signal?: AbortSignal,
): Promise<{ status: number; body: { dokument: Dokument; dublette: boolean } }> {
  return hochladen<{ dokument: Dokument; dublette: boolean }>(`/v/${seg(V)}/chargen/${seg(chargeId)}/dokumente`, datei, {
    beiFortschritt,
    signal,
  });
}
export const holeEingang = (V: string) => getJson<EingangListe>(`/v/${seg(V)}/eingang`);
export const importiereEingang = (V: string, charge_id: Uuid, dateien: string[]) =>
  postJson<ImportErgebnis>(`/v/${seg(V)}/eingang/import`, { charge_id, dateien });
export const holeDokumente = (V: string, charge?: Uuid | null) =>
  getJson<{ eintraege: Dokument[] }>(`/v/${seg(V)}/dokumente${frage({ charge })}`);
export const holeDokument = (ref: string) => getJson<DokumentVoll>(`/dokumente/${seg(ref)}`);
/** Nur Admins (Recht `original_pdf`); `grund` 10..200 Zeichen, wird serverseitig protokolliert. */
export const originalUrl = (ref: string, grund: string) => `/api/dokumente/${seg(ref)}/original${frage({ grund })}`;
export const holeSeite = (id: Uuid) => getJson<Seite>(`/seiten/${seg(id)}`);
/** `rev` (render_rev) nur als Teil der Adresse: ein neu gerastertes Bild bekommt eine neue (Review 01.10.). */
export const seitenBildUrl = (id: Uuid, breite: number, variante?: "maskiert" | "original", grund?: string, rev?: number | null) =>
  `/api/seiten/${seg(id)}/bild${frage({ variante, breite, grund: variante === "original" ? grund : undefined, rev })}`;
export const dreheSeite = (id: Uuid, grad: 90 | 180 | 270) => putJson<SeiteKurz>(`/seiten/${seg(id)}/drehen`, { grad });
export const holeOffeneMasken = (V: string, nach?: string | null, limit?: number) =>
  getJson<Seite<OffeneMaske>>(`/v/${seg(V)}/masken/offen${frage({ nach, limit })}`);
export const setzeSeitenMaske = (id: Uuid, rechtecke: Rechteck[]) =>
  putJson<SeiteKurz & { neu_lesen?: boolean }>(`/seiten/${seg(id)}/maske`, { rechtecke });
export const gibSeiteFrei = (id: Uuid, grund: string) => postJson<SeiteKurz>(`/seiten/${seg(id)}/maske/freigeben`, { grund });
/** E19: Namensfeld-Erkennung (lokal, Tesseract, keine KI) für schon gerasterte Seiten nachholen —
 *  idempotent, Recht `maskieren`, serverseitig protokolliert. */
export const erkenneNamensfelder = (V: string) => postJson<ErkennenErgebnis>(`/v/${seg(V)}/masken/erkennen`);

/* --- Lauf (02 §2.4) ------------------------------------------------------------- */

export const holeLauf = (V: string) => getJson<LaufStand>(`/v/${seg(V)}/lauf`);
export const starteLauf = (V: string, b: { schritte?: Schritt[]; seiten?: Uuid[]; nur_neu: boolean }) =>
  postJson<LaufStand>(`/v/${seg(V)}/lauf`, b);
export const stoppeLauf = (V: string) => postJson<LaufStand>(`/v/${seg(V)}/lauf/stoppen`);
export const holeGpu = () => getJson<Gpu>("/gpu");

/* --- Aufträge (Kopfleiste: alle Hintergrundarbeit eines Verfahrens) --------------------- */

export const holeAuftraege = (V: string) => getJson<AuftraegeStand>(`/v/${seg(V)}/auftraege`);

/* --- Kästchenmessung (E23, 02 §2.4a) ------------------------------------------------ */

/** Gütekarte (Recht `lesen`): Kästchen, Übereinstimmung mit dem Modell und mit geprüften Antworten. */
export const holeKaestchenGuete = (V: string) => getJson<KaestchenGuete>(`/v/${seg(V)}/kaestchen`);
/** Gelesene Seiten ohne aktuelle Messung einreihen (Recht `lauf`, idempotent, protokolliert). */
export const messeKaestchen = (V: string) => postJson<{ eingereiht: number }>(`/v/${seg(V)}/kaestchen/messen`);
/** E33: „Vorlagen lernen“ — einreihen, wenn sich die Seiten seit dem letzten Lernen geändert haben (0|1). */
export const lerneVorlagen = (V: string) => postJson<{ eingereiht: number }>(`/v/${seg(V)}/rahmen/lernen`);
/** E33: gelernte Seitenarten und Registrierung (nur Zahlen). */
export const holeRahmen = (V: string) => getJson<RahmenStand>(`/v/${seg(V)}/rahmen`);
/** Die aktuellen Messungen einer Seite (auch die Kästchen außerhalb jeder Region). */
export const holeSeitenKaestchen = (id: Uuid) => getJson<SeitenKaestchen>(`/seiten/${seg(id)}/kaestchen`);

/* --- Seitenanalyse (E37, 02 §2.4d) ----------------------------------------------------- */

/** Zahlen der Seitenanalyse (Recht `lesen`): Elemente je Art, gefundene Zeichnungen/Karten, vom Modell übersehen. */
export const holeSeitenanalyse = (V: string) => getJson<SeitenanalyseStand>(`/v/${seg(V)}/seitenanalyse`);
/** Gelesene Seiten ohne aktuelle Analyse einreihen (Recht `lauf`, idempotent, protokolliert). */
export const analysiereSeiten = (V: string) => postJson<{ eingereiht: number }>(`/v/${seg(V)}/seitenanalyse/nachziehen`);
/** Elemente einer Seite (Ebene „Elemente“ der Dokumentansicht) — nur Art, Rahmen, Status. */
export const holeSeitenElemente = (id: Uuid) => getJson<SeitenElemente>(`/seiten/${seg(id)}/elemente`);
/** Prüfen → „Zeichnungen“ (Recht `pruefen`). */
export const holeZeichnungen = (V: string, status: "offen" | "uebernommen" | "verworfen" | "alle" = "offen") =>
  getJson<ZeichnungenListe>(`/v/${seg(V)}/zeichnungen${frage({ status })}`);
export const holeZeichnung = (id: Uuid) => getJson<ZeichnungItem>(`/zeichnungen/${seg(id)}`);
export const uebernimmZeichnung = (id: Uuid, k: { dinge: string[]; beschriftungen: number[]; feld_key?: string | null }) =>
  postJson<{ id: Uuid; status: "uebernommen"; regionen: RegionAufSeite[] }>(`/zeichnungen/${seg(id)}/uebernehmen`, k);
export const verwirfZeichnung = (id: Uuid) => postJson<{ id: Uuid; status: "verworfen" }>(`/zeichnungen/${seg(id)}/verwerfen`);

/* --- Prüfen (02 §2.5) ------------------------------------------------------------- */

export interface WarteschlangenFilter extends Blaettern {
  grund?: string | null;
  status?: string | null;
  charge?: string | null;
}
export const holeWarteschlange = (V: string, p: WarteschlangenFilter = {}) =>
  getJson<Seite<PruefEintrag> & { fortschritt: { geprueft: number; gesamt: number } }>(
    `/v/${seg(V)}/pruefen/warteschlange${frage({ grund: p.grund, status: p.status, charge: p.charge, nach: p.nach, limit: p.limit })}`,
  );
export const holePruefItem = (regionId: Uuid, gold: boolean) => getJson<PruefItem>(`/pruefen/${seg(regionId)}${frage({ gold: gold ? 1 : 0 })}`);
export const speicherePruefung = (regionId: Uuid, a: PruefAktion) =>
  postJson<PruefItem & { naechster: string | null }>(`/pruefen/${seg(regionId)}`, a);
export const setzeBbox = (regionId: Uuid, b: { x: number; y: number; w: number; h: number; basis_geom_rev: number; grund?: string }) =>
  putJson<RegionAufSeite & { geom_rev: number }>(`/regionen/${seg(regionId)}/bbox`, b);
export const legeRegionAn = (seiteId: Uuid, b: { bbox: { x: number; y: number; w: number; h: number }; art: string; frage_key?: string | null; basis_render_rev: number }) =>
  postJson<RegionAufSeite>(`/seiten/${seg(seiteId)}/regionen`, b);
export const zieheRegionZurueck = (regionId: Uuid, b: { grund: string; ersetzt_durch?: Uuid[] }) =>
  postJson<RegionAufSeite>(`/regionen/${seg(regionId)}/zurueckziehen`, b);
export const setzeFlags = (regionId: Uuid, b: { personenbezug?: boolean; freigegeben_extern?: boolean }) =>
  putJson<RegionFlags>(`/regionen/${seg(regionId)}/flags`, b);
export interface AntwortWert {
  option_code?: string | null;
  skala_wert?: number | null;
  zahl?: number | null;
}
export const setzeAntwortwerte = (regionId: Uuid, b: { werte: AntwortWert[]; zustand: Zustand; basis_rev: number }) =>
  postJson<PruefItem>(`/regionen/${seg(regionId)}/antwortwerte`, b);
export const bestaetigeZeichnung = (regionId: Uuid, b: { dinge: string[]; beschreibung?: string | null; basis_rev: number }) =>
  postJson<PruefItem>(`/regionen/${seg(regionId)}/zeichnung`, b);
export const loescheBelegInhalt = (regionId: Uuid, grund: string) =>
  postJson<{ ok: boolean }>(`/regionen/${seg(regionId)}/inhalt_loeschen`, { grund });
export const holeVerlauf = (regionId: Uuid) => getJson<{ revisionen: Revision[] }>(`/regionen/${seg(regionId)}/verlauf`);
/* Prüf-Entwurf (02 §2.5.1, E22): Autosave der Prüfmaske. Kinderschrift — nur im Speicher, nie im
   Browserspeicher. Schlüssel: Region + Modus (`?gold=1`, wie GET /pruefen/{id}).

   Reihenfolge ist hier Sache EINER Stelle: jeder Schreibvorgang (PUT, DELETE) einer Region trägt
   sich in `ausstehend` ein. Ein GET wartet, bis sie durch sind (sonst läse eine schnelle Rückkehr
   — j, dann k — einen Stand, den der keepalive-PUT gleich überschreibt), ein DELETE ebenso (sonst
   legte ein später ankommender PUT den verworfenen Entwurf neu an). Zwischen PUTs ordnet der
   Server über `stand` (monoton je Browser): ein älterer PUT, der einen neueren überholt, ändert
   dort nichts.

   „Verwerfen“ löscht mit einer kurzen Frist (`ENTWURF_FRIST.verwerfenMs`), damit „Rückgängig“
   geht (zweimal Esc ist ein Reflex). Wer die Region vorher wieder öffnet, bekommt sie frisch: der
   GET führt das Löschen zuerst aus. Beim Verlassen der Seite geht es sofort (keepalive). Ein
   gescheitertes Löschen (offline) holt der nächste GET nach — der verworfene Entwurf kehrt nie
   wieder. */
export const ENTWURF_FRIST = { verwerfenMs: 8000 };
const entwurfSchluessel = (regionId: Uuid, gold: boolean) => `${regionId}:${gold ? 1 : 0}`;
const ausstehend = new Map<string, Set<Promise<unknown>>>();
function vormerken<T>(k: string, p: Promise<T>): Promise<T> {
  const menge = ausstehend.get(k) ?? new Set<Promise<unknown>>();
  ausstehend.set(k, menge);
  const still = p.then(
    () => undefined,
    () => undefined,
  );
  menge.add(still);
  void still.then(() => {
    menge.delete(still);
    if (!menge.size && ausstehend.get(k) === menge) ausstehend.delete(k);
  });
  return p;
}
async function abwarten(k: string): Promise<void> {
  const menge = ausstehend.get(k);
  if (menge?.size) await Promise.all([...menge]);
}
/** Stand-Zähler (µs seit 1970, streng steigend); ein gesehener größerer Stand zieht ihn nach. */
let standZaehler = 0;
function naechsterStand(): number {
  standZaehler = Math.max(standZaehler + 1, Date.now() * 1000);
  return standZaehler;
}
function standGesehen(s: unknown): void {
  if (typeof s === "number" && Number.isFinite(s) && s > standZaehler) standZaehler = s;
}
/** Verworfen, aber noch nicht gelöscht (Frist läuft oder das DELETE scheiterte). */
const verworfen = new Map<string, { regionId: Uuid; gold: boolean; bis: number; uhr: ReturnType<typeof setTimeout> | null }>();
function loeschenJetzt(k: string, keepalive = false): Promise<void> {
  const v = verworfen.get(k);
  if (!v) return Promise.resolve();
  if (v.uhr !== null) clearTimeout(v.uhr);
  v.uhr = null;
  const { regionId, gold } = v;
  const p = abwarten(k)
    .then(() => deleteJson<void>(`/regionen/${seg(regionId)}/entwurf${frage({ gold: gold ? 1 : null })}`, { keepalive }))
    .then(() => {
      if (verworfen.get(k) === v) verworfen.delete(k);
    });
  return vormerken(k, p);
}
if (typeof window !== "undefined")
  window.addEventListener("pagehide", () => {
    for (const k of [...verworfen.keys()]) void loeschenJetzt(k, true).catch(() => undefined);
  });

export async function holeEntwurf(regionId: Uuid, gold = false): Promise<Entwurf | null> {
  const k = entwurfSchluessel(regionId, gold);
  await abwarten(k);
  if (verworfen.has(k)) {
    // Verworfen heißt verworfen: erst löschen; scheitert es, trotzdem nichts zurückbringen.
    await loeschenJetzt(k).catch(() => undefined);
    return null;
  }
  try {
    const e = await getJson<Entwurf | null>(`/regionen/${seg(regionId)}/entwurf${frage({ gold: gold ? 1 : null })}`);
    if (!(e && typeof e.basis_rev === "number" && e.daten && typeof e.daten === "object")) return null;
    standGesehen(e.stand);
    return e;
  } catch (f) {
    if (f instanceof ApiError && f.status === 404) return null;
    throw f;
  }
}
/** PUT, idempotent. `keepalive` für das Wegspeichern beim Verlassen der Seite (pagehide): dieselbe
 *  Anfrage wie sonst (X-HS, same-origin) — sendBeacon kann X-HS nicht setzen. `uebernommen: false`
 *  = der Server hatte schon einen neueren Stand (die Antwort zieht den Zähler nach). */
export function speichereEntwurf(
  regionId: Uuid,
  gold: boolean,
  b: { daten: EntwurfDaten; basis_rev: number },
  keepalive = false,
): Promise<{ geaendert_am: string; uebernommen: boolean }> {
  const k = entwurfSchluessel(regionId, gold);
  const stand = naechsterStand();
  // Wer wieder schreibt, hat nicht verworfen: ein noch ausstehendes Löschen fällt weg.
  const v = verworfen.get(k);
  if (v?.uhr) clearTimeout(v.uhr);
  verworfen.delete(k);
  const p = putJson<{ geaendert_am: string; stand?: number }>(`/regionen/${seg(regionId)}/entwurf${frage({ gold: gold ? 1 : null })}`, { ...b, stand }, keepalive ? { keepalive: true } : {}).then((r) => {
    standGesehen(r?.stand);
    return { geaendert_am: r?.geaendert_am ?? new Date().toISOString(), uebernommen: typeof r?.stand !== "number" || r.stand <= stand };
  });
  return vormerken(k, p);
}
/** Sofort löschen — nach allen laufenden PUTs dieser Region. `vorRev`: nur einen Entwurf auf einer
 *  Revision < vorRev (veraltet), nie einen gültigen neueren (der eigene Item-Stand kann alt sein). */
export function verwirfEntwurf(regionId: Uuid, gold = false, vorRev?: number): Promise<void> {
  const k = entwurfSchluessel(regionId, gold);
  const p = abwarten(k).then(() => deleteJson<void>(`/regionen/${seg(regionId)}/entwurf${frage({ gold: gold ? 1 : null, vor_rev: vorRev ?? null })}`, { keepalive: true }));
  return vormerken(k, p);
}
/** „Verwerfen“: nach der Frist löschen (Rückgängig bis dahin). Vorher Laufendes wird abgewartet. */
export function verwerfeEntwurfMitFrist(regionId: Uuid, gold = false): void {
  const k = entwurfSchluessel(regionId, gold);
  const alt = verworfen.get(k);
  if (alt?.uhr) clearTimeout(alt.uhr);
  const ms = ENTWURF_FRIST.verwerfenMs;
  const v = { regionId, gold, bis: Date.now() + ms, uhr: null as ReturnType<typeof setTimeout> | null };
  verworfen.set(k, v);
  v.uhr = setTimeout(() => void loeschenJetzt(k).catch(() => undefined), ms);
}
/** Läuft für diese Region noch die Frist? Dann die Restzeit in ms, sonst 0. */
export function verwerfenRest(regionId: Uuid, gold = false): number {
  const v = verworfen.get(entwurfSchluessel(regionId, gold));
  return v && v.uhr !== null ? Math.max(0, v.bis - Date.now()) : 0;
}
/** „Rückgängig“: das Löschen absagen, solange die Frist läuft. true, wenn es abgesagt wurde. */
export function verwerfenZuruecknehmen(regionId: Uuid, gold = false): boolean {
  const k = entwurfSchluessel(regionId, gold);
  const v = verworfen.get(k);
  if (!v || v.uhr === null) return false;
  clearTimeout(v.uhr);
  verworfen.delete(k);
  return true;
}
export const holeStichprobe = (V: string) => getJson<Stichprobe>(`/v/${seg(V)}/pruefen/stichprobe`);
export const zieheStichprobe = (V: string) => postJson<Stichprobe>(`/v/${seg(V)}/pruefen/stichprobe`);
export const holeGold = (V: string) => getJson<{ seiten: { seite_id: Uuid; fertig: boolean }[] }>(`/v/${seg(V)}/gold`);
export const markiereGold = (V: string, seiten: Uuid[]) =>
  postJson<{ seiten: { seite_id: Uuid; fertig: boolean }[] }>(`/v/${seg(V)}/gold`, { seiten });

/* --- Bogenvorlage aus dem leeren Bogen (02 §2.4c, E35) — Recht pruefen (Zurückziehen: verwalten) ---- */

/** Stand: Schalter, Blätter, Entwurf (Zahlen), Bogenvorlage, Schritte — nur Zahlen und Kennwörter. */
export const holeFormular = (V: string) => getJson<FormularStand>(`/v/${seg(V)}/formular`);
/** Basisadresse des leeren Bogens; die Bühne hängt `?w=400|1400|2400` an (Audit je Abruf, 409 ohne Freigabe). */
export const formularBildBasis = (V: string, blattId: Uuid) => `/api/v/${seg(V)}/formular/blatt/${seg(blattId)}/bild`;
export const holeFormularEntwurf = (V: string) => getJson<FormularEntwurf>(`/v/${seg(V)}/formular/entwurf`);
/** Autosave (E22-Muster): jede Änderung mit `basis_rev`; 409 `veraltet` {aktuell:{rev}}, 400 mit Satz. */
export const bearbeiteFormularEntwurf = (V: string, basis_rev: number, ops: FormularOp[]) =>
  patchJson<{ rev: number; ops: Record<string, number> }>(`/v/${seg(V)}/formular/entwurf`, { basis_rev, ops });
export const formularVorschau = (V: string, basis_rev: number) =>
  postJson<FormularVorschau>(`/v/${seg(V)}/formular/entwurf/vorschau`, { basis_rev });
/** 400 `festschreiben_nicht_moeglich` trägt `gruende` (deutsche Sätze), 409 `veraltet`/`vorlage_vorhanden`. */
export const festschreibenFormular = (V: string, basis_rev: number) =>
  postJson<FormularFestschreibenErgebnis>(`/v/${seg(V)}/formular/entwurf/festschreiben`, { basis_rev });
/** „Bogenvorlage lesen“ (202): leeren Bogen bauen, dann je Blatt lesen. 409 `formular_aus`, `blatt_bestaetigt`. */
export const leseFormular = (V: string, blatt_ids?: Uuid[] | null, neu = false) =>
  postJson<{ leerbogen: number; lesen: number; uebersprungen: number }>(`/v/${seg(V)}/formular/lesen`, blatt_ids ? { blatt_ids, neu } : { neu });
export const bearbeiteFormularVorlage = (V: string, ops: Record<string, unknown>[]) =>
  patchJson<Record<string, number>>(`/v/${seg(V)}/formular/vorlage`, { ops });
export const zieheFormularZurueck = (V: string) => postJson<Record<string, number>>(`/v/${seg(V)}/formular/zurueckziehen`);

/* --- Fragen-Abgleich (02 §2.5.2, E25) ------------------------------------------- */

export const holeFragenliste = (V: string) => getJson<Fragenliste>(`/v/${seg(V)}/fragen`);
export const speichereFragenliste = (V: string, basis_rev: number, fragen: FrageEingabe[], ki_vorschlag?: Uuid | null) =>
  putJson<Fragenliste>(`/v/${seg(V)}/fragen`, ki_vorschlag ? { basis_rev, fragen, ki_vorschlag } : { basis_rev, fragen });
export const uebernimmFragenliste = (V: string, basis_rev: number) =>
  postJson<{ fragenliste: Fragenliste; ergebnis: FragenErgebnis }>(`/v/${seg(V)}/fragen/uebernehmen`, { basis_rev });
export const setzeFragenlisteZurueck = (V: string, basis_rev: number) =>
  postJson<Fragenliste>(`/v/${seg(V)}/fragen/zuruecksetzen`, { basis_rev });
/** E31: Stand von „KI ordnet die Fragen“ und der letzte Vorschlag. */
export const holeFragenKi = (V: string) => getJson<FragenKi>(`/v/${seg(V)}/fragen/ki`);
/** E34: „Sichere Fragen automatisch übernehmen“ an/aus (Recht pruefen) — Antwort: die Fragenliste. */
export const setzeFragenAutomatisch = (V: string, an: boolean) => putJson<Fragenliste>(`/v/${seg(V)}/fragen/automatisch`, { an });
/** E34: Bestand der Antworten für das Band der Auswertung (Recht lesen). */
export const holeAuswertungsBestand = (V: string) => getJson<AuswertungBestand>(`/v/${seg(V)}/auswertung/bestand`);
/** E31: „Mit KI ordnen“ — wartet hinter einem laufenden Lauf (202). */
export const starteFragenKi = (V: string) => postJson<{ eingereiht: boolean; ki: FragenKi }>(`/v/${seg(V)}/fragen/ki`);

/* --- Bögen (02 §2.6) ------------------------------------------------------------ */

export const holeBogenVorschlaege = (V: string, p: { charge?: string | null } & Blaettern = {}) =>
  getJson<BogenVorschlagSeite>(`/v/${seg(V)}/boegen/vorschlaege${frage({ charge: p.charge, nach: p.nach, limit: p.limit })}`);
/** E36: „Eindeutige Bögen automatisch übernehmen“ an/aus (Recht prüfen). */
export const setzeBoegenAutomatisch = (V: string, an: boolean) => putJson<BoegenAutomatisch>(`/v/${seg(V)}/boegen/automatisch`, { an });
export const bestaetigeBoegen = (V: string, gruppen: BogenGruppe[]) =>
  postJson<{ boegen: { id: Uuid; code: string; regionen_codes: string[] }[] }>(`/v/${seg(V)}/boegen/bestaetigen`, { gruppen });
export const holeBoegen = (V: string, f: Filter, nach?: string | null, limit?: number) =>
  getJson<Seite<BogenKurz>>(`/v/${seg(V)}/boegen${frage({ filter: filterB64(f), nach, limit })}`);
export const holeBogen = (ref: string) => getJson<BogenAnsicht>(`/boegen/${seg(ref)}`);
export interface BogenPatch {
  art?: "einzel" | "gruppe";
  anzahl_kinder?: number | null;
  zaehlt?: boolean;
  ausschluss_grund?: string | null;
  duplikat_von?: string | null;
  schutzhinweis?: boolean;
}
export const aendereBogen = (id: Uuid, patch: BogenPatch) => patchJson<BogenAnsicht>(`/boegen/${seg(id)}`, patch);
export const zieheBogenZurueck = (id: Uuid, grund: string) => postJson<BogenAnsicht>(`/boegen/${seg(id)}/zurueckziehen`, { grund });
export const setzeMerkmal = (id: Uuid, merkmal: string, wert: string | null) =>
  putJson<BogenAnsicht>(`/boegen/${seg(id)}/merkmale/${seg(merkmal)}`, { wert, quelle: "manuell" });

/* --- IIIF, Permalinks, Belege (02 §2.7–§2.8) ------------------------------------ */

export const iiifUrl = (seite: Uuid, region: string, size: string, fmt: "webp" | "jpg" | "png" = "webp") =>
  `/api/iiif/${seg(seite)}/${region}/${size}/0/default.${fmt}`;
export const ausschnittUrl = (ref: string, b: number, fmt: "webp" | "jpg" | "png" = "webp") =>
  `/api/belege/${seg(ref)}/ausschnitt.${fmt}${frage({ b })}`;
export const aufloesen = (ref: string) => getJson<Aufloesung>(`/aufloesen/${seg(ref)}`);
export const holeBeleg = (ref: string, p: { rev?: number | null; n?: number | null; aus?: string | null } = {}) =>
  getJson<BelegAnsicht>(`/belege/${seg(ref)}${frage({ rev: p.rev, n: p.n, aus: p.aus })}`);
export const holeSeiteImDokument = (ref: string, pdfSeite: number) =>
  getJson<SeiteMitRegionen>(`/dokumente/${seg(ref)}/seiten/${seg(pdfSeite)}`);
export const holeVerwendung = (ref: string) => getJson<{ eintraege: Verwendung[] }>(`/belege/${seg(ref)}/verwendung`);

/* --- Themen (02 §2.9) ------------------------------------------------------------ */

export const holeThemen = (V: string, katalog: "aktuell" | "entwurf" | number) =>
  getJson<Katalog>(`/v/${seg(V)}/themen${frage({ katalog: String(katalog) })}`);
export const legeEntwurfAn = (V: string, von: "geruest" | "aktuell" | string) => postJson<Katalog>(`/v/${seg(V)}/themen/entwurf`, { von });
export interface ThemaEingabe {
  label: string;
  oberthema: string;
  definition?: string | null;
  einschluss?: string | null;
  ausschluss?: string | null;
  anker?: string[];
  ord?: number;
}
export const setzeEntwurfThema = (V: string, slug: string, b: ThemaEingabe) => putJson<Thema>(`/v/${seg(V)}/themen/entwurf/${seg(slug)}`, b);
export const entferneEntwurfThema = (V: string, slug: string) => deleteJson<{ ok: boolean }>(`/v/${seg(V)}/themen/entwurf/${seg(slug)}`);
export const starteInduktiv = (V: string, stichprobe: number) =>
  postJson<{ auftrag_id: Uuid }>(`/v/${seg(V)}/themen/entwurf/induktiv`, { stichprobe });
export const friereEin = (V: string, notiz?: string | null) =>
  postJson<Katalog>(`/v/${seg(V)}/themen/einfrieren`, notiz ? { notiz } : {});
export const setzeMapping = (V: string, b: { von_version: number; zuordnungen: { alt_slug: string; neu_slug: string }[] }) =>
  postJson<{ ok: boolean }>(`/v/${seg(V)}/themen/mapping`, b);
export const holeThemenVorschlaege = (V: string, p: { status?: "offen" | "alle" } & Blaettern = {}) =>
  getJson<Seite<ThemaVorschlag>>(`/v/${seg(V)}/themen/vorschlaege${frage({ status: p.status, nach: p.nach, limit: p.limit })}`);
export const uebernimmVorschlag = (id: Uuid, aenderungen?: Partial<ThemaEingabe>) =>
  postJson<Thema>(`/vorschlaege/${seg(id)}/uebernehmen`, aenderungen ? { aenderungen } : {});
export const lehneVorschlagAb = (id: Uuid) => postJson<{ ok: boolean }>(`/vorschlaege/${seg(id)}/ablehnen`);
export const holeThema = (V: string, slug: string) => getJson<ThemaSeite>(`/v/${seg(V)}/themen/${seg(slug)}`);
export const holeKodierWarteschlange = (V: string, p: { status?: string | null; thema?: string | null } & Blaettern = {}) =>
  getJson<Seite<KodierEintrag> & { fortschritt: { bestaetigt: number; gesamt: number } }>(
    `/v/${seg(V)}/kodierung/warteschlange${frage({ status: p.status, thema: p.thema, nach: p.nach, limit: p.limit })}`,
  );
export const kodiere = (nennungId: Uuid, b: { themen: string[]; aktion: "bestaetigen" | "ablehnen"; basis_hash: string }) =>
  postJson<KodierErgebnis>(`/nennungen/${seg(nennungId)}/kodierung`, b);
export const teileNennung = (nennungId: Uuid, spannen: [number, number][]) =>
  postJson<{ nennungen: { id: Uuid; code: string | null; text: string }[] }>(`/nennungen/${seg(nennungId)}/teilen`, { spannen });

/* --- Auswertung, Zählwerk (02 §2.10) --------------------------------------------- */

export const holeAuswertungsKatalog = (V: string) => getJson<AuswertungsKatalog>(`/v/${seg(V)}/auswertung/katalog`);
export const holeKacheln = (V: string) =>
  getJson<{ kpi: KachelDef[]; ueberblick: KachelDef[]; matrix: KachelDef }>(`/v/${seg(V)}/auswertung/kacheln`);
export const zaehle = (V: string, rezept: Rezept) => postJson<Ergebnis>(`/v/${seg(V)}/zaehlen`, { rezept });
export const holeKennzahlen = (V: string, filter: Filter) => postJson<Kennzahlen>(`/v/${seg(V)}/kennzahlen`, { filter });
export const holeFacetten = (V: string, filter: Filter) => postJson<Facetten>(`/v/${seg(V)}/facetten`, { filter });
export interface AntwortenAbfrage extends Blaettern {
  filter: Filter;
  spalten: string[];
  sort?: "code" | "bogen" | "frage";
}
export const holeAntworten = (V: string, p: AntwortenAbfrage) =>
  getJson<Seite<Belegkarte & { spalten: Record<string, string> }> & { gesamt: number }>(
    `/v/${seg(V)}/antworten${frage({ filter: filterB64(p.filter), spalten: p.spalten.join(","), sort: p.sort, nach: p.nach, limit: p.limit })}`,
  );
export const holeZitate = (V: string, p: { thema: string; filter: Filter } & Blaettern) =>
  getJson<Seite<Belegkarte & { thema_slug: string }>>(
    `/v/${seg(V)}/zitate${frage({ thema: p.thema, filter: filterB64(p.filter), nach: p.nach, limit: p.limit })}`,
  );
/** Suche per POST mit Körper {q} — Suchwörter gehören nie in eine Adresse (Zugriffsprotokolle, S7). */
export const suche = (V: string, q: string) =>
  postJson<{ treffer_code: Aufloesung | null; texte: (Belegkarte & { rang: number })[] }>(`/v/${seg(V)}/suche`, { q });
export const speichereAbfrage = (V: string, titel: string, rezept: Rezept) =>
  postJson<{ id: Uuid; titel: string; rezept: Rezept; rezept_hash: string; url: string }>(`/v/${seg(V)}/abfragen`, { titel, rezept });
export const holeAbfragen = (V: string) => getJson<{ eintraege: AbfrageKurz[] }>(`/v/${seg(V)}/abfragen`);
export const holeAbfrage = (id: Uuid) => getJson<{ id: Uuid; titel: string; rezept: Rezept; ergebnis: Ergebnis }>(`/abfragen/${seg(id)}`);

/* --- Belegmengen (02 §2.11) ------------------------------------------------------- */

export const erzeugeMenge = (V: string, rezept: Rezept, zelle: Zelle) => postJson<MengeKopf>(`/v/${seg(V)}/mengen`, { rezept, zelle });
export const holeMenge = (code: string) => getJson<Menge>(`/mengen/${seg(code)}`);
export const holeMengenElemente = (code: string, nach?: string | null, limit = 12) =>
  getJson<Seite<MengenElement>>(`/mengen/${seg(code)}/elemente${frage({ nach, limit })}`);
/** POST seit dem Review: materialisiert die heutige Menge (X-HS wie jede Änderung). */
export const holeMengeHeute = (code: string) => postJson<MengeHeute>(`/mengen/${seg(code)}/heute`);
export const mengeExcelUrl = (code: string) => `/api/mengen/${seg(code)}/excel`;

/* --- Live (02 §2.10.1) ------------------------------------------------------------- */

export const holeStand = (V: string, seit?: number | null) => getJson<StandEreignis>(`/v/${seg(V)}/stand${frage({ seit })}`);
export const liveUrl = (V: string) => `/api/v/${seg(V)}/live`;

/* --- Exporte (02 §2.12) ------------------------------------------------------------ */

export const erzeugeExport = (V: string, w: ExportWunsch) => postJson<ExportKopf>(`/v/${seg(V)}/exporte`, w);
export const holeExporte = (V: string, nach?: string | null, limit?: number) =>
  getJson<Seite<ExportKopf>>(`/v/${seg(V)}/exporte${frage({ nach, limit })}`);
export const holeExport = (code: string) => getJson<Export>(`/exporte/${seg(code)}`);
export const exportDateiUrl = (code: string) => `/api/exporte/${seg(code)}/datei`;
export const holeKennzahlDiff = (code: string, kennzahl: string) => getJson<KennzahlDiff>(`/exporte/${seg(code)}/k/${seg(kennzahl)}`);
export const erneuereExport = (code: string) =>
  postJson<ExportKopf & { vorgaenger: string; inhalt_gleich: boolean }>(`/exporte/${seg(code)}/neu`);
export const protokolliereDiagramm = (V: string, b: { rezept: Rezept; titel: string; format: string; seitenformat: string }) =>
  postJson<{ code: string; fusszeile: string }>(`/v/${seg(V)}/exporte/diagramm`, b);

/* --- Assistent (02 §2.13) ------------------------------------------------------------ */

export const holeBereiche = (V: string) => getJson<{ bereiche: Bereich[] }>(`/assistent/bereiche${frage({ v: V })}`);
export const holeKiStatus = () => getJson<KiStatus>("/ki/status");
/** 202; Fehler werden geschluckt — Aufwärmen ist ein Gefallen, keine Pflicht. */
export const waermeKi = () => postJson<{ ok: boolean }>("/ki/warm").catch(() => ({ ok: false }));
export const holeAntwort = (id: Uuid) => getJson<AntwortAnsicht>(`/antworten/${seg(id)}`);
export const hefteAntwortAn = (id: Uuid) => postJson<{ angeheftet: boolean; verfaellt_am: string | null }>(`/antworten/${seg(id)}/anheften`);
export const antwortExcel = (id: Uuid) => postJson<ExportKopf>(`/antworten/${seg(id)}/excel`);

/* --- Ortsverzeichnis (E21, 05 §15.5) ------------------------------------------------- */
// Nur öffentliche Daten (OSM, Ebenen des Eigentümers, Eingaben). Nicht-öffentliche Orte und
// Straßen auf der Karte nur mit dem Recht `orte` (Befund 5) — der Server filtert.

export const holeOrteStand = (V: string) => getJson<OrteStand>(`/v/${seg(V)}/orte/stand`);
export interface OrteAbfrage {
  q?: string | null;
  art?: OrtArt | null;
  stadtteil?: string | null;
  quelle?: "eigen" | "osm" | "hand" | null;
  aktiv?: boolean;
  nach?: string | null;
  n?: number;
}
/** Mit `q` gerankt (wie die Kandidatensuche), ohne Blättern; sonst nach Namen mit `weiter`. */
export const holeOrte = (V: string, p: OrteAbfrage = {}) =>
  getJson<Seite<Ort>>(
    `/v/${seg(V)}/orte${frage({ q: p.q, art: p.art, stadtteil: p.stadtteil, quelle: p.quelle, aktiv: p.aktiv === false ? "false" : null, nach: p.nach, n: p.n })}`,
  );
export interface OrtNeu {
  name: string;
  art: OrtArt;
  lon?: number;
  lat?: number;
  lage_von?: Uuid;
  oeffentlich?: boolean;
  ortsbezirk_id?: Uuid;
}
export const legeOrtAn = (V: string, b: OrtNeu) => postJson<Ort>(`/v/${seg(V)}/orte`, b);
export interface OrtPatch {
  name?: string;
  art?: OrtArt;
  oeffentlich?: boolean;
  aktiv?: boolean;
  lon?: number;
  lat?: number;
  /** null = Stadtteil wieder automatisch (Punkt-in-Fläche) */
  ortsbezirk_id?: Uuid | null;
  bezirk_fest?: boolean;
}
export const aendereOrt = (V: string, id: Uuid, b: OrtPatch) => patchJson<Ort>(`/v/${seg(V)}/orte/${seg(id)}`, b);
export const legeOrtAliasAn = (V: string, ortId: Uuid, alias: string) => postJson<{ id: Uuid; alias: string }>(`/v/${seg(V)}/orte/${seg(ortId)}/aliase`, { alias });
export const legeBezirkAliasAn = (V: string, bezirkId: Uuid, alias: string) =>
  postJson<{ id: Uuid; alias: string }>(`/v/${seg(V)}/ortsbezirke/${seg(bezirkId)}/aliase`, { alias });
export const deaktiviereAlias = (V: string, aliasId: Uuid) => patchJson<{ ok: boolean }>(`/v/${seg(V)}/aliase/${seg(aliasId)}`, { aktiv: false });
export const holeOrtsbezirke = (V: string, aktiv?: boolean) =>
  getJson<Ortsbezirk[]>(`/v/${seg(V)}/ortsbezirke${frage({ aktiv: aktiv === undefined ? null : String(aktiv) })}`);
export const legeBezirkAusOrtAn = (V: string, ortId: Uuid) => postJson<Ortsbezirk>(`/v/${seg(V)}/ortsbezirke`, { aus_ort: ortId });
export const aendereBezirk = (V: string, id: Uuid, b: { name?: string; aktiv?: boolean }) => patchJson<{ ok: boolean }>(`/v/${seg(V)}/ortsbezirke/${seg(id)}`, b);
export const holeSchulorte = (V: string) => getJson<Schulort[]>(`/v/${seg(V)}/schulorte`);
/** `ort_id: null` = „Lage unbekannt“ (die Schule bleibt ohne Ort, kein Hinweis für die Suche). */
export const setzeSchulort = (V: string, schule: string, ort_id: Uuid | null) => putJson<{ ok: boolean }>(`/v/${seg(V)}/schulorte`, { schule, ort_id });
/** Overpass sieht hier nur den getippten Kommunennamen — nie Text eines Kindes (E21d). */
export const sucheGemeinden = (V: string, name: string) => postJson<Gemeinde[]>(`/v/${seg(V)}/orte/osm/gemeinden`, { name });
export const ladeOsm = (V: string, relation_id: number, umfang: OsmUmfang[]) =>
  postJson<{ auftrag_id: Uuid }>(`/v/${seg(V)}/orte/osm/import`, { relation_id, umfang });
/** Je Quelle (OSM, Datei, Kopie) der jüngste Import samt Phase, Zahlen oder Fehlersatz — für den Reiter „Laden“. */
export const holeOrteImport = (V: string) => getJson<{ importe: OrteImporte }>(`/v/${seg(V)}/orte/import`);
/** GeoJSON (≤ 20 MB) hochladen → Vorschau; geschrieben wird erst mit `importiereOrtsdatei`. */
export function ladeOrtsdateiHoch(
  V: string,
  datei: File,
  beiFortschritt?: (geladen: number, gesamt: number) => void,
  signal?: AbortSignal,
): Promise<{ status: number; body: DateiVorschau }> {
  return hochladen<DateiVorschau>(`/v/${seg(V)}/orte/datei`, datei, { beiFortschritt, signal });
}
export const importiereOrtsdatei = (V: string, dateiId: Uuid, b: DateiImport) =>
  postJson<{ auftrag_id: Uuid }>(`/v/${seg(V)}/orte/datei/${seg(dateiId)}/import`, b);
export const uebernimmOrte = (V: string, von: string) => postJson<{ auftrag_id: Uuid }>(`/v/${seg(V)}/orte/uebernehmen`, { von });
/** E29 — „Aus Datenbank laden“: Quelle → Datenbank → Tabelle → Vorschau → Import (CPU-Auftrag ortsimport). */
export const holeOrteDatenquellen = (V: string) => getJson<{ eintraege: DatenquelleKurz[] }>(`/v/${seg(V)}/orte/datenquellen`);
export const holeQuellDatenbanken = (V: string, quelle: string) => getJson<QuellDatenbanken>(`/v/${seg(V)}/orte/datenquellen/${seg(quelle)}/datenbanken`);
export const holeQuellTabellen = (V: string, quelle: string, datenbank: string) =>
  getJson<QuellTabellen>(`/v/${seg(V)}/orte/datenquellen/${seg(quelle)}/tabellen${frage({ datenbank })}`);
export const holeQuellVorschau = (V: string, quelle: string, t: { datenbank: string; schema: string; tabelle: string; spalte: string }) =>
  getJson<DbVorschau>(`/v/${seg(V)}/orte/datenquellen/${seg(quelle)}/vorschau${frage({ datenbank: t.datenbank, schema: t.schema, tabelle: t.tabelle, spalte: t.spalte })}`);
export const importiereAusDatenquelle = (V: string, quelle: string, b: DbImport) =>
  postJson<{ auftrag_id: Uuid }>(`/v/${seg(V)}/orte/datenquellen/${seg(quelle)}/import`, b);
/** GeoJSON der Karte. `fetchJson` holt mit `cache: "no-store"` — ETag/304 greift hier NICHT;
 *  neu geholt wird die Karte nur, wenn sich das Verzeichnis ändert (useLive, Befund F21) oder
 *  nach eigenen Änderungen. `strassen` nur mit Recht `orte`. */
export const holeOrteKarte = (V: string, strassen = false) => getJson<KarteGeo>(`/v/${seg(V)}/orte/karte${frage({ mit: strassen ? "strassen" : null })}`);
/** E32 (05 §19.3): Ebene „Beiträge“ der Karte — je öffentlichem Ort die Kinder mit bestätigter
 *  Ortsangabe. Der Filter wie bei GET …/antworten; ohne Filter fehlt der Parameter ganz. */
export const holeOrteBeitraege = (V: string, f: Filter) =>
  getJson<BeitraegeGeo>(`/v/${seg(V)}/orte/beitraege${frage({ filter: Object.keys(kompakt(f)).length ? filterB64(f) : null })}`);

/* --- Verortung prüfen (E21c, 05 §15.5) ------------------------------------------------- */

export interface VerortungFilter {
  status?: "vorgeschlagen" | "bestaetigt" | "abgelehnt";
  sicherheit?: Sicherheit | null;
  bezug?: Bezug | null;
  unklar?: boolean | null;
  /** E21h: nur Zeilen des Ortsteil-Feldes */
  quelle?: "ki" | "mensch" | "ortsteilfeld" | null;
  nach?: string | null;
  n?: number;
}
export const holeVerortungWarteschlange = (V: string, p: VerortungFilter = {}) =>
  getJson<{ posten: VerortungPosten[]; weiter: string | null }>(
    `/v/${seg(V)}/verortung/warteschlange${frage({ status: p.status, sicherheit: p.sicherheit, bezug: p.bezug, unklar: p.unklar === null || p.unklar === undefined ? null : String(p.unklar), quelle: p.quelle, nach: p.nach, n: p.n })}`,
  );
/** Sammelannahme: nur vorgeschlagen + sicher + kein Wohnort + eindeutiges aktives Ziel, keine Straße. */
export const nimmVerortungenAn = (V: string, ids: Uuid[]) => postJson<{ angenommen: number; uebersprungen: number }>(`/v/${seg(V)}/verortung/sammel`, { ids });
export const holeRegionVerortung = (regionId: Uuid) => getJson<RegionVerortung>(`/regionen/${seg(regionId)}/verortung`);
export interface VerortungNeu {
  feld: "wortlaut" | "lesefassung";
  start: number;
  ende: number;
  ort_id?: Uuid;
  ortsbezirk_id?: Uuid;
  bezug: Bezug;
}
export const legeVerortungAn = (regionId: Uuid, b: VerortungNeu) => postJson<Verortung>(`/regionen/${seg(regionId)}/verortung`, b);
export const entscheideVerortung = (regionId: Uuid, verortungId: Uuid, b: { aktion: VerortungAktion; ort_id?: Uuid; ortsbezirk_id?: Uuid; bezug?: Bezug }) =>
  postJson<Verortung>(`/regionen/${seg(regionId)}/verortung/${seg(verortungId)}/entscheiden`, b);
